// src/pages/admin/AdminDashboard.tsx
import { useState } from "react";
import { cn } from "@/lib/utils";
import { FileText, Users, Shield, LayoutDashboard, Database } from "lucide-react";

// Sections
import { UsersSection } from "./UsersSection";
import { ReportsSection } from "./ReportsSection";
import { PermissionsSection } from "./PermissionsSection";
import { AdminSyncSection } from "./AdminSyncSection";

type Tab = "rapports" | "utilisateurs" | "permissions" | "synchronisation";
type SubTab = "gestion" | "ajouter";

export default function AdminDashboard() {
  const [tab, setTab] = useState<Tab>("rapports");
  const [subRapports, setSubRapports] = useState<SubTab>("gestion");
  const [subUsers, setSubUsers] = useState<SubTab>("gestion");

  return (
    <div className="min-h-screen bg-background">
      {/* Bandeau */}
      <div className="bg-gradient-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-primary/20" />
        <div className="relative max-w-7xl mx-auto px-6 py-6">
          <div className="text-center space-y-2 animate-in fade-in-0 slide-in-from-top-4 duration-500">
            <div className="inline-flex items-center justify-center gap-3">
              <div className="p-2 rounded-lg bg-white/10 ring-1 ring-white/20 backdrop-blur-sm">
                <LayoutDashboard className="h-5 w-5" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold">Dashboard Administrateur</h1>
            </div>
            <p className="text-white/80 max-w-2xl mx-auto text-sm md:text-base">
              Gérez les rapports, utilisateurs et permissions.
            </p>
          </div>

          {/* Onglets principaux */}
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <AdminTabButton
              icon={<FileText className="h-4 w-4" />}
              active={tab === "rapports"}
              onClick={() => setTab("rapports")}
              label="Rapports"
            />
            <AdminTabButton
              icon={<Users className="h-4 w-4" />}
              active={tab === "utilisateurs"}
              onClick={() => setTab("utilisateurs")}
              label="Utilisateurs"
            />
            <AdminTabButton
              icon={<Shield className="h-4 w-4" />}
              active={tab === "permissions"}
              onClick={() => setTab("permissions")}
              label="Permissions"
            />
            <AdminTabButton
              icon={<Database className="h-4 w-4" />}
              active={tab === "synchronisation"}
              onClick={() => setTab("synchronisation")}
              label="Synchronisation"
            />
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {tab === "rapports" && (
          <ReportsSection subTab={subRapports} setSubTab={setSubRapports} />
        )}
        {tab === "utilisateurs" && (
          <UsersSection subTab={subUsers} setSubTab={setSubUsers} />
        )}
        {tab === "permissions" && <PermissionsSection />}
        {tab === "synchronisation" && <AdminSyncSection />}
      </div>
    </div>
  );
}

/* ------------------------------- UI helpers ------------------------------- */

function AdminTabButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-4 py-2 border text-sm transition-all duration-200",
        active
          ? "bg-white/95 text-foreground shadow-md scale-105"
          : "bg-white/10 text-white/90 hover:bg-white/20 hover:scale-105"
      )}
    >
      {icon}
      {label}
    </button>
  );
}
